import {
	ProductApi,
	OpeningHoursWithExceptions,
	StartTimeCount,
	StartTimeSlots,
	BookingBuffer,
	OpeningHourTimesWithMoments,
} from 'common/types';
import { AvailabilityRange } from 'common/api/frontend/inventory_new/availabilityRange';
import { getStartEndRangeFromTimeSlots, getTimeSlotsForDateRange } from './utils';
import { getStockProductMaintenanceMinutes } from 'common/utils/productUtils';
import moment from 'moment';
import {
	AvailabilityByTime,
	TimeSlotType,
	TimeSlotVariant,
	ValidityWithReason,
	ValidSlotsByTime,
} from './types';
import { getRelevantRanges } from '../utils';
import { getAvailabilityForTimeSlotWithReason } from './availabilityForTimeSlot';

export const getTimeSlots = (props: {
	start: moment.Moment;
	end: moment.Moment;
	openingHours: OpeningHoursWithExceptions;
	variant: TimeSlotVariant;
	startTimeSlots: StartTimeSlots;
	useBookingBuffer: BookingBuffer | undefined;
	product: ProductApi;
	stockProducts: ProductApi[];
	timeSlotType: TimeSlotType;
	availabilities: AvailabilityRange[];
	startTimeCounts: StartTimeCount;
	alreadySelected?: number;
}) => {
	const {
		start,
		end,
		openingHours,
		variant,
		startTimeSlots,
		useBookingBuffer,
		product,
		stockProducts,
		timeSlotType,
		availabilities,
		startTimeCounts,
		alreadySelected,
	} = props;
	const timeSlots = getTimeSlotsWithoutAvailabilities({
		start,
		end,
		openingHours,
		variant,
		startTimeSlots,
		useBookingBuffer,
		timeSlotType: timeSlotType.type,
	});
	return addAvailabilityToTimeSlots({
		timeSlots,
		product,
		stockProducts,
		timeSlotType,
		availabilities,
		startTimeCounts,
		startTimeSlots,
		alreadySelected,
		openingHours,
	});
};

export const getTimeSlotsWithoutAvailabilities = (props: {
	start: moment.Moment;
	end: moment.Moment;
	openingHours: OpeningHoursWithExceptions;
	variant: TimeSlotVariant;
	startTimeSlots: StartTimeSlots;
	useBookingBuffer: BookingBuffer | undefined;
	timeSlotType: TimeSlotType['type'];
}): ValidSlotsByTime => {
	const {
		start,
		end,
		openingHours,
		startTimeSlots,
		useBookingBuffer,
		variant,
		timeSlotType,
	} = props;
	const timeSlots = getTimeSlotsForDateRange({
		startDate: start,
		endDate: end,
		startTimeSlots,
		openingHours,
		bookingBuffer: useBookingBuffer,
		variant,
		timeSlotType,
	});
	return timeSlots;
};

export const addAvailabilityToTimeSlots = (props: {
	product: ProductApi;
	stockProducts: ProductApi[];
	timeSlotType: TimeSlotType;
	timeSlots: ValidSlotsByTime;
	availabilities: AvailabilityRange[];
	startTimeCounts: StartTimeCount;
	openingHours: OpeningHoursWithExceptions;
	startTimeSlots: StartTimeSlots;
	alreadySelected?: number;
}): AvailabilityByTime => {
	const {
		product,
		timeSlotType,
		timeSlots,
		availabilities,
		stockProducts,
		openingHours,
		startTimeSlots,
		startTimeCounts,
		alreadySelected,
	} = props;
	const inventoryBlockerMinutesBefore = 0;
	const inventoryBlockerMinutesAfter = getStockProductMaintenanceMinutes(product, stockProducts);

	const startTimesRange = getStartEndRangeFromTimeSlots(timeSlots, timeSlotType, openingHours);

	const relevantRanges =
		Object.keys(timeSlots).length > 0 && !!startTimesRange.start && !!startTimesRange.end
			? getRelevantRanges({
					start: startTimesRange.start.toDate(),
					end: startTimesRange.end.toDate(),
					availabilityRanges: availabilities,
			  })
			: [];

	const orderLimit = startTimeSlots.orderLimit ?? Infinity;
	const usesFixedTimesOutsideOpeningHours =
		startTimeSlots?.type === 'fixed' && !!startTimeSlots?.outsideOpeningHours;

	// This helper is to reduce the amount of valid ranges to compare against
	// The logic is: returns valid ranges from last check, and if end time of last range is still after the next end time, we can use this small subset of ranges for comparing
	let rangesToCompareFirst: AvailabilityRange[] | undefined = !!relevantRanges.length
		? [relevantRanges[0]]
		: undefined;
	const openingTimesByDate = new Map<string, OpeningHourTimesWithMoments | null>();

	const getAvailabilityForDate = (dayString: string) => {
		const startTimes = timeSlots[dayString];
		return Object.entries(startTimes).reduce((result, [time, validityWithReason]) => {
			const { availability, overlappingRanges } = getAvailabilityForTimeSlotWithReason({
				timeWithReason: { day: dayString, time, validity: validityWithReason },
				timeSlotType,
				openingHours,
				openingTimesByDate,
				orderLimit,
				startTimeCounts,
				usesFixedTimesOutsideOpeningHours,
				availabilityRanges: relevantRanges,
				availabilityRangesToCompareFirst: rangesToCompareFirst,
				inventoryBlockerMinutesBefore,
				inventoryBlockerMinutesAfter,
				alreadySelected: alreadySelected ?? 0,
			});
			if (overlappingRanges) {
				rangesToCompareFirst = overlappingRanges;
			}
			return {
				...result,
				[time]: availability,
			};
		}, {} as { [time: string]: ValidityWithReason });
	};

	const dates = Object.keys(timeSlots);
	const availabilityByStartTime = dates.reduce((result, date) => {
		result[date] = getAvailabilityForDate(date);
		return result;
	}, {});
	return availabilityByStartTime;
};
